import React, { useCallback, useEffect, useState } from 'react';
import { Image, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { fmtTime, minutesToHm } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { humanize } from '../../../lib/date';
import { errorMessage } from '../../../lib/errors';
import { getCurrentPosition, type GeoFix } from '../../../lib/geo';
import { useNow } from '../../../lib/hooks';
import { pickImage, takeSelfie } from '../../../lib/picker';
import { punchSource } from '../../../lib/platform';
import { useMut } from '../../../lib/query';
import { appendFile, type UploadFile } from '../../../lib/upload';
import { Alert, Badge, Button, Card, Columns, EmptyState, Icon, Loading, PageHeader, Row, Screen, Stack, StatusBadge, Switch, Text, useTheme } from '../../../ui';

export function PunchScreen() {
  const { me } = useAuth();
  const { colors } = useTheme();
  const selfieRequired = !!me?.tenant?.settings?.attendance?.selfie_required;
  const today = useQuery({ queryKey: ['attendance', 'today'], queryFn: () => api.attendance.today(), refetchInterval: 60_000 });
  const now = useNow();
  const [geo, setGeo] = useState<GeoFix | null>(null);
  const [geoErr, setGeoErr] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const [useSelfie, setUseSelfie] = useState(selfieRequired);
  const [selfie, setSelfie] = useState<UploadFile | null>(null);
  const [camErr, setCamErr] = useState<string | null>(null);

  const locate = useCallback(async () => {
    setGeoErr(null);
    setLocating(true);
    try {
      setGeo(await getCurrentPosition());
    } catch (e) {
      setGeoErr(errorMessage(e));
    } finally {
      setLocating(false);
    }
  }, []);
  useEffect(() => {
    void locate();
  }, [locate]);

  const capture = async (fromLibrary?: boolean) => {
    setCamErr(null);
    try {
      const f = fromLibrary ? await pickImage() : await takeSelfie();
      if (f) setSelfie(f);
    } catch (e) {
      setCamErr(errorMessage(e));
    }
  };

  const punch = useMut(
    () => {
      const fd = new FormData();
      fd.append('source', punchSource);
      if (geo) {
        fd.append('latitude', String(geo.latitude));
        fd.append('longitude', String(geo.longitude));
      }
      if (useSelfie || selfieRequired) appendFile(fd, 'selfie', selfie);
      return api.attendance.punch(fd);
    },
    {
      invalidate: [['attendance'], ['dashboard']],
      success: (r) => `Punch recorded at ${fmtTime(r.punch.punched_at)}${r.punch.within_geofence === false ? ' (outside geofence)' : ''}`,
      onSuccess: () => setSelfie(null),
    },
  );

  if (today.isLoading) return <Loading />;
  const punches = today.data?.punches ?? [];
  const isIn = punches.length % 2 === 1;
  const shift = today.data?.shift;
  const day = today.data?.day;
  const wantSelfie = useSelfie || selfieRequired;
  const blocked = wantSelfie && !selfie;

  return (
    <Screen refreshing={today.isRefetching} onRefresh={() => today.refetch()} testID="punch-screen">
      <PageHeader title="Punch in / out" subtitle={shift ? `Shift ${shift.name}: ${shift.start_time} – ${shift.end_time}` : 'No shift assigned for today'} />
      <Columns>
        <Card>
          <Stack gap={18}>
            <View>
              <Text variant="muted">{now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</Text>
              <Text size={44} weight="bold" style={{ letterSpacing: -1, fontVariant: ['tabular-nums'] }}>
                {now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </Text>
              <Row>
                <Badge tone={isIn ? 'success' : 'neutral'}>{isIn ? 'Punched in' : 'Punched out'}</Badge>
                {day && <StatusBadge status={day.status} />}
                {day?.is_late && <Badge tone="warning">Late by {day.late_minutes}m</Badge>}
              </Row>
            </View>
            <Row wrap={false}>
              <Icon name="mapPin" color={colors.muted} />
              <View style={{ flex: 1 }}>
                {geo ? (
                  <Text>
                    {geo.latitude.toFixed(5)}, {geo.longitude.toFixed(5)} <Text variant="small">(±{Math.round(geo.accuracy)} m)</Text>
                  </Text>
                ) : (
                  <Text variant="muted">{locating ? 'Locating…' : geoErr ?? 'Location unavailable'}</Text>
                )}
              </View>
              <Button size="sm" variant="ghost" icon="refresh" title="Refresh" loading={locating} onPress={locate} />
            </Row>
            {geoErr && <Alert tone="warning">{`${geoErr}. You can still punch; it will be recorded without location.`}</Alert>}
            <View style={{ gap: 12 }}>
              <Switch label={`Attach selfie${selfieRequired ? ' (required)' : ''}`} value={wantSelfie} disabled={selfieRequired} onChange={setUseSelfie} />
              {wantSelfie && (
                <Row>
                  {selfie && <Image source={{ uri: selfie.uri }} style={{ width: 96, height: 96, borderRadius: 12, borderWidth: 1, borderColor: colors.border }} accessibilityLabel="Selfie preview" />}
                  <Button icon="camera" title={selfie ? 'Retake' : 'Take selfie'} onPress={() => capture(false)} />
                  <Button icon="image" variant="ghost" title="Choose photo" onPress={() => capture(true)} />
                </Row>
              )}
              {camErr && <Alert tone="warning">{camErr}</Alert>}
            </View>
            <Button variant={isIn ? 'danger' : 'success'} size="lg" block icon="clock" title={isIn ? 'Punch out' : 'Punch in'} loading={punch.isPending} disabled={blocked} onPress={() => punch.mutate(undefined)} testID="punch-button" />
          </Stack>
        </Card>
        <Stack>
          <Card title="Today's punches">
            {punches.length === 0 ? (
              <EmptyState icon="clock" title="No punches yet" />
            ) : (
              <Stack gap={10}>
                {punches.map((p, i) => (
                  <Row key={p.id} justify="space-between">
                    <Row>
                      <Badge tone={i % 2 === 0 ? 'success' : 'neutral'}>{i % 2 === 0 ? 'IN' : 'OUT'}</Badge>
                      <Text weight="semibold">{fmtTime(p.punched_at)}</Text>
                    </Row>
                    <Text variant="small">
                      {humanize(p.source)}
                      {p.within_geofence === false ? ' · outside geofence' : ''}
                    </Text>
                  </Row>
                ))}
              </Stack>
            )}
          </Card>
          {day && (
            <Card title="Summary">
              <Stack gap={8}>
                <Row justify="space-between"><Text variant="muted">Worked</Text><Text weight="semibold">{minutesToHm(day.worked_minutes)}</Text></Row>
                <Row justify="space-between"><Text variant="muted">Overtime</Text><Text weight="semibold">{minutesToHm(day.ot_minutes)}</Text></Row>
                <Row justify="space-between"><Text variant="muted">Early exit</Text><Text weight="semibold">{day.early_exit_minutes}m</Text></Row>
              </Stack>
            </Card>
          )}
        </Stack>
      </Columns>
    </Screen>
  );
}
